import { BeforeAfterGallery, Pair, PairGif, Procedure } from '@nrwl/rx-models';
import { Observable, Subject } from 'rxjs';
import {
  RxListCompositeComponent,
  RxReactiveFormCompositeComponent,
  RxTextCompositeComponent,
  RxVideoCompositeComponent,
} from '@nrwl/rx-forms';

import { DialogsConfigService } from '../dialogs-config.service';
import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { rxRequiredValidator } from '@nrwl/rx-validators';

@Injectable({
  providedIn: 'root',
})
export class GalleriesDialogConfigService extends DialogsConfigService {
  constructor() {
    super();
  }

  public newGroup() {
    return {
      payload: {
        title: 'Create New Gallery Category',
        buttons: this.SubmitDismissButtons(),
        data: new Procedure(),
      },
    };
  }

  public editGroup(category: Procedure) {
    return {
      panelClass: 'gallery-group-settings-popup',
      payload: {
        title: 'Gallery Group Settings',
        buttons: this.SubmitDismissButtons(),
        data: category,
      },
    };
  }

  public editNote(pair: Pair) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Edit description',
        fields: [
          {
            placeholder: 'Description',
            labelText: null,
            hasInlineInfo: false,
            value: pair.procedure_details,
          },
        ],
        buttons: this.SubmitDismissButtons('Save', 'Dismiss'),
      },
    };
  }

  public createGallery() {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Add New Gallery',
        buttons: this.SubmitDismissButtons('Save', 'Dismiss'),
        fields: [
          {
            placeholder: 'Gallery Name',
            labelText: null,
            hasInlineInfo: true,
            value: '',
            maxLength: 50,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
            // asyncValidators: [this.formsValidatorsService.categoryUniqValidator(SearchType.AccountLibrary)]
          },
        ],
      },
    };
  }

  public editGallery(gallery: BeforeAfterGallery) {
    return {
      width: 820,
      panelClass: 'scrollable-dialog',
      payload: {
        title: 'Gallery Settings',
        data: gallery,
        buttons: this.SubmitDismissButtons('Save Changes', 'Cancel'),
      },
    };
  }

  public watermarkSettings(gallery: BeforeAfterGallery) {
    return {
      bodyClasses: 'watermark-popup',
      panelClass: 'scrollable-dialog',
      payload: {
        title: 'Custom Watermark Settings',
        data: gallery,
        buttons: this.SubmitDismissCustomSubButtons('Save', 'Close', 'Preview'),
      },
    };
  }

  public confirmDelete(name: string) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirmation required',
        tip: `Are you sure you want to delete this ${name}?`,
        tipClose: true,
        buttons: this.SubmitDismissButtons('Confirm', 'Dismiss'),
      },
    };
  }

  public restoreLastConsultation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: `You have recent incomplete Consultation. Do you want to continue?`,
        tipClose: true,
        buttons: this.SubmitDismissButtons('Yes', 'No'),
      },
    };
  }

  public confirmSaveChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: `You have unsaved changes. `,
        tipClose: true,
        buttons: this.SubmitDenyButtons('Save', 'Discard'),
      },
    };
  }

  public confirmMergePhotos(itemsCount = 0) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `Merge ${itemsCount} Photo${itemsCount > 1 ? 's' : ''} into Bundle`,
        tip: `All selected photos will appear under one bundle`,
        tipClose: true,
        buttons: this.SubmitDismissButtons('Merge', 'Cancel'),
      },
    };
  }

  public confirmSharingToApp(toggle: boolean) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm',
        tip: `Are you sure you want to ${toggle ? 'publish' : 'unpublish'} this gallery to the app?`,
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public confirmSharingToWeb(toggle: boolean) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm',
        tip: `Are you sure you want to ${toggle ? 'publish' : 'unpublish'} this gallery to the web?`,
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public moveGallery(items$: Observable<Procedure[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Move to',
        items$: items$,
        icon: 'icon-gallery',
        buttons: this.SubmitDismissButtons('Move'),
      },
    };
  }

  public cloneGallery(items$: Observable<Procedure[]>, action = 'Clone') {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: `${action} to`,
        icon: 'icon-gallery',
        items$: items$,
        buttons: this.SubmitDismissButtons(`${action}`),
      },
    };
  }

  public selectGroup(items$: Observable<Procedure[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Select Group',
        items$: items$,
        buttons: this.SubmitDismissButtons('Select'),
      },
    };
  }

  public splitBundleConfirmation(pairLength: number) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `Split ${pairLength} Photos into Single Photos`,
        tip: 'All selected objects will appear as a single photos',
        buttons: this.SubmitDismissButtons('Split', 'Cancel'),
      },
    };
  }

  public confirmMove() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirmation required',
        tip: 'Are you sure you want to move this gallery?',
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public confirmResetChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm changes',
        tip: 'Unsaved changes may be lost',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public confirmLeavingPage() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm Navigation',
        tip: 'You have unsaved changes. Are you sure you want to leave this page?',
        buttons: this.SubmitDismissButtons('Leave this Page', 'Stay on this Page'),
      },
    };
  }

  public showPairTemplates(title = 'Download options', submitLabel = 'Download') {
    return {
      payload: {
        title,
        buttons: this.SubmitDismissButtons(submitLabel, 'Cancel'),
      },
    };
  }

  public gifOptions(pair: Pair) {
    return {
      payload: {
        title: 'Edit photo pair',
        value: pair.as_slider ? 'slider' : 'morph',
        pair: pair,
        buttons: this.SubmitDismissCutomButtons('Download', 'Close', 'Add to Gallery'),
      },
    };
  }

  public shareOptions(pair: Pair) {
    return {
      hideDialogClose: false,
      payload: {
        title: 'Sharing',
        tip: 'Choose Social Network To Share',
        pair,
        buttons: this.DismissButton('Close'),
      },
    };
  }

  public uploadPair(gallery = 0) {
    return {
      panelClass: 'uploadDialog',
      hideHeader: true,
      hideFooter: true,
      component: RxTextCompositeComponent,
      payload: {
        galleryId: gallery,
      },
    };
  }

  public warningPatientPrivacy() {
    return {
      component: RxTextCompositeComponent,
      hideDialogClose: false,
      payload: {
        title: 'Warning: You are sharing this photo to Social Media',
        tip: 'Do you have a signed consent form on file from this patient?',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public helpCenter() {
    return {
      component: RxVideoCompositeComponent,
      payload: {
        title: 'Help Center',
        buttons: this.DismissButton(),
      },
    };
  }
}
